import { PassengerField, passengerFields, wizardModes } from 'Models/Enums';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { PassengerDetail } from './PassengerDetail';
import { Journey } from 'Global/Journey/Types/Journey';
import {
	FreightBookingWizardData,
	freightBookingWizardDataHasChanged,
	getDefaultFreightWizardData,
} from './FreightBookingWizardData';
import {
	getDefaultPassengerWizardData,
	PassengerBookingWizardData,
	passengerBookingWizardDataHasChanged,
} from './PassengerBookingWizardData';
import {
	getDefaultVehicleWizardData,
	VehicleBookingWizardData,
	vehicleBookingWizardDataHasChanged,
} from './VehicleBookingWizardData';
import { TabSelected, TripType } from '../BookingWizardTypes';
import { BulkBookingFilters } from 'Modules/BulkBooking/Types';

export type BookingWizardInnerDataType =
	| FreightBookingWizardData
	| PassengerBookingWizardData
	| VehicleBookingWizardData;

/**
 * A view of the inner wizard data as its passenger-ticket slots, keyed by the property names
 * `PassengerTypeStore.getWizardDataProperty` resolves to.
 *
 * `PassengerBookingWizardData` and `VehicleBookingWizardData` both declare all eight slots;
 * `FreightBookingWizardData` declares none, because freight bookings carry no passengers. That
 * is why this is a separate `Partial` view rather than a member of the union above — indexing
 * the union directly would require the slots to exist on the freight variant too.
 */
type PassengerTicketFields = Partial<Record<PassengerField, PassengerDetail[]>>;

/**
 * Read one passenger type's tickets off the inner wizard data.
 *
 * Returns `undefined` — not an empty array — when the slot is unset, matching how callers
 * already guard with `?.length ?? 0` and `?.some(...)`.
 */
export function getPassengerTickets(
	innerWizardData: BookingWizardInnerDataType,
	field: PassengerField,
): PassengerDetail[] | undefined {
	return (innerWizardData as PassengerTicketFields)[field];
}

/** Write one passenger type's tickets onto the inner wizard data. */
export function setPassengerTickets(
	innerWizardData: BookingWizardInnerDataType,
	field: PassengerField,
	tickets: PassengerDetail[],
): void {
	(innerWizardData as PassengerTicketFields)[field] = tickets;
}

/**
 * A copy of `wizardData` whose passenger tickets share nothing with the original.
 *
 * <p>`new BookingWizardData({ ...data })` looks like a copy and is not a deep one: every wizard-data
 * constructor is an `Object.assign`, so the new object gets the *same* eight arrays, holding the
 * *same* `PassengerDetail` instances. Setting `driver` on a ticket of the copy therefore sets it on
 * the original too.</p>
 *
 * <p>That is how `oldWizardData` stopped being a record of anything: it is the "before" the wizard
 * compares against to decide whether a booking has changed, and it moved whenever the live data
 * did — so passenger edits compared equal. Worse, it was intermittent. `getOldWizardData` rehydrates
 * from sessionStorage through `JSON.parse`, which <em>is</em> a deep copy, so the two were
 * independent after a page reload and aliased within a session.</p>
 */
export function copyWizardDataWithOwnTickets(wizardData: BookingWizardData): BookingWizardData {
	const copy = new BookingWizardData({ ...wizardData });

	passengerFields.forEach(field => {
		const tickets = getPassengerTickets(copy.innerWizardData, field);
		if (tickets) {
			setPassengerTickets(
				copy.innerWizardData,
				field,
				tickets.map(ticket => new PassengerDetail({ ...ticket })),
			);
		}
	});

	return copy;
}

/**
 * Applies a patch to every passenger on the booking, across all eight type slots.
 *
 * <p>Shared rather than repeated because two steps now set a class — the ticket cards set one for
 * the whole party, and the seating step sets one per passenger — and they have to write it the same
 * way or the second overwrites what the first did.</p>
 *
 * <p>Returns a new wizard data; the caller saves it. `patchFor` returning null leaves that passenger
 * untouched, which is what lets a caller change some and not others.</p>
 */
export function patchAllPassengers(
	wizardData: BookingWizardData,
	patchFor: (passenger: PassengerDetail) => Partial<PassengerDetail> | null,
): BookingWizardData {
	const next = new BookingWizardData({ ...wizardData });

	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(next.innerWizardData, field);
		if (!tickets) {
			return;
		}
		setPassengerTickets(
			next.innerWizardData,
			field,
			tickets.map(ticket => {
				const patch = patchFor(ticket);
				return patch ? { ...ticket, ...patch } : ticket;
			}),
		);
	});

	return next;
}

/**
 * A string that changes whenever a passenger's class or seat does.
 *
 * <p>For dependency arrays. The sidebar re-prices off a hand-written list of wizard fields, and the
 * per-passenger ones are not in it — they live inside eight arrays rather than as a field of their
 * own, so nothing in that list moves when a booker changes class. Both affect the price: the class
 * sets the fare, and the seat decides whether the selection fee is charged.</p>
 *
 * <p>A signature rather than the array itself, because the arrays are rebuilt on every edit and would
 * re-fire the fetch on every keystroke in an unrelated field.</p>
 */
export function passengerSeatingSignature(wizardData: BookingWizardData | undefined): string {
	if (!wizardData) {
		return '';
	}

	const parts: string[] = [];
	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(wizardData.innerWizardData, field);
		(tickets ?? []).forEach(ticket => {
			parts.push(
				`${ticket.id}:${ticket.passengerClassId ?? ''}:${ticket.returningPassengerClassId ?? ''}`
					+ `:${ticket.seatId ?? ''}:${ticket.returningSeatId ?? ''}`,
			);
		});
	});

	return parts.join('|');
}

/** One traveller, as the two things that decide what they pay on a sailing. */
export interface PassengerFare {
	/** Their passenger type (A-H) — adult, child, and the five the site may configure. */
	typeKey: string;
	/** The fare type they are in on the leg asked about, or undefined where they have none yet. */
	passengerClassId?: string;
}

/**
 * Every traveller on the booking, paired with the fare type they hold on one leg.
 *
 * <p>The counts alone cannot price a party that is split: "two adults" costs one figure in Economy
 * and another across Economy and Business, and a booking being edited routinely arrives split
 * because the seating step lets each passenger choose. This keeps them apart so a sailing can be
 * priced as the booking actually stands rather than as if everybody shared one fare type.</p>
 */
/**
 * Which passenger field holds a leg's fare type.
 *
 * <p>Editing only the return leg stores that leg in the departing fields (<c>departureTrip ===
 * false</c>) — the seating step and the submission both read <c>passengerClassId</c> there. The
 * ticket step still calls it the return leg, for the date rules, so it asks this before reading or
 * writing a fare type rather than looking at <c>returningPassengerClassId</c>, which is empty.</p>
 */
export const fareFieldLeg = (wizardData: BookingWizardData, leg: 'departure' | 'return'): 'departure' | 'return' =>
	leg === 'return' && wizardData.departureTrip === false ? 'departure' : leg;

export function passengerFaresByLeg(
	wizardData: BookingWizardData,
	leg: 'departure' | 'return',
): PassengerFare[] {
	const fares: PassengerFare[] = [];
	const fareLeg = fareFieldLeg(wizardData, leg);

	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(wizardData.innerWizardData, field);
		(tickets ?? []).forEach(ticket => {
			fares.push({
				typeKey: passengerType.passengerTypeKey,
				passengerClassId: fareLeg === 'return' ? ticket.returningPassengerClassId : ticket.passengerClassId,
			});
		});
	});

	return fares;
}

/** How many passengers of each type the booking carries, keyed by the type's key (A-H). */
export function passengerCountsByType(wizardData: BookingWizardData): Record<string, number> {
	const counts: Record<string, number> = {};

	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(wizardData.innerWizardData, field);
		if (tickets && tickets.length > 0) {
			counts[passengerType.passengerTypeKey] = tickets.length;
		}
	});

	return counts;
}

export class BookingWizardData<T = BookingWizardInnerDataType> {
	wizardMode: wizardModes;
	restrictedMode: boolean;
	/**
	 * Why this booking's journey can't be changed, or undefined when it can — set from the booking
	 * being edited, see `convertBookedBookingToWizardData`. Covers a partially checked-in booking
	 * (check-in statuses belong to a route, and the gate has already begun processing it) and a
	 * discounted one (the discount doesn't survive a re-quote).
	 *
	 * Carried on the wizard data rather than recomputed because the wizard doesn't hold the booking
	 * entity. Optional because wizard state persisting in sessionStorage predates the field; the
	 * server guard in `DetectJourneyChangeStep` is the real boundary.
	 */
	journeyChangeBlockedReason?: string;
	acceptedTsAndCs: boolean;
	/**
	 * key = id of WizardStepEntity,
	 * value = indicates whether user has accepted
	 */
	acceptedTsAndCsMap: Record<string, boolean> = {};
	/**
	 * Used in ferry filter.
	 *
	 * The start date that defines the date range on the wizard sidebar filter.
	 */
	startDate: Date;
	/**
	 * Used in ferry filter.
	 *
	 * The end date that defines the date range on the wizard sidebar filter.
	 */
	endDate: Date;
	/**
	 * Used in ferry filter.
	 */
	tabSelected: TabSelected;
	/**
	 * Used in ferry filter.
	 */
	tripType: TripType;
	/**
	 * True once the user has explicitly chosen one-way or return. When set, switching between
	 * vehicle and walk-on preserves the user's choice instead of reverting to the white-label
	 * default. Freight still forces one-way regardless.
	 */
	userSelectedTripType: boolean = false;

	// Location ids. These are used to determine the route
	fromLocationId: string;
	toLocationId: string;

	// Feature: Multi-stop - create booking
	departingJourney?: Journey;
	returningJourney?: Journey;

	bookingToEdit?: string;
	/**
	 * The returning leg's own booking, when the edit covers both trips.
	 *
	 * <p>A return journey is two bookings, and anything asked of a sailing "excluding the booking
	 * being edited" has to name the booking on <b>that</b> sailing. Asking the returning sailing to
	 * exclude the departing booking excludes nothing, so the returning leg's own seats came back as
	 * taken by someone else and were cleared off the booking.</p>
	 */
	returnBookingToEdit?: string;
	/**
	 * True means changes apply to one-way using the departure of booking.
	 * False means changes apply to one-way using the return of booking.
	 * Undefined means changes apply to both ways of booking.
	 */
	departureTrip?: boolean;

	// Ticket Ids
	departureTicketId: string;
	returningTicketId?: string;

	// Id for user the booking is assigned to
	userId: string;

	/**
	 * Marketing-subscription value captured by the "Subscribe to marketing" checkbox on
	 * the cart page.
	 */
	marketingSubscriptionAcknowledgment?: boolean;

	// Ticket selection dates. These are the dates shown on the departing and returning tabs on the ticket selection
	// page.  These are NOT the dates shown on the sidebar
	ticketSelectionStartDate: Date;
	ticketSelectionEndDate: Date;

	bulkBookingFilters?: BulkBookingFilters;

	// Fees
	departingCancellationFee?: number;
	returningCancellationFee?: number;
	departingCancellationCutoffHours?: number;
	/**
	 * Undefined means wizard is editing two-way booking.
	 * If departureTrip is true, date is the departure time of the return booking.
	 * If departureTrip is false, date is the departure time of the departure booking.
	 */
	associatedTripDateTime?: Date;

	/**
	 * If this boolean is true, it will automatically apply add-ons to both directions of the booking.
	 * It will start as true if it is a return booking and false if it is a departure booking.
	 */
	automaticallyApplyAddOnsToBothDirections: boolean;

	/**
	 * If this is true, the previous vehicle type that was on the booking was disabled in the backend and we want to
	 * let the user know that is why their vehicle type is no longer available.
	 */
	disabledVehicleType: boolean;

	innerWizardData: BookingWizardInnerDataType;

	constructor(data: Partial<BookingWizardData<T>>) {
		Object.assign(this, data);
		if (isNotNullOrUndefined(this.innerWizardData)) {
			if (this.innerWizardData.type === 'vehicle') {
				this.innerWizardData = new VehicleBookingWizardData(this.innerWizardData);
			} else if (this.innerWizardData.type === 'passenger') {
				this.innerWizardData = new PassengerBookingWizardData(this.innerWizardData);
			} else if (this.innerWizardData.type === 'freight') {
				this.innerWizardData = new FreightBookingWizardData(this.innerWizardData);
			}
		}

		// Hydrate dates that may have been deserialized as strings
		if (this.bulkBookingFilters) {
			// Wizard state persists in sessionStorage, so a user mid-wizard when a deploy lands
			// rehydrates filters saved before the repeat pattern existed. Those searches were all
			// weekly-every-week, which is what these defaults reconstruct.
			if (!this.bulkBookingFilters.frequency) {
				this.bulkBookingFilters.frequency = 'weekly';
			}
			if (!this.bulkBookingFilters.interval || this.bulkBookingFilters.interval < 1) {
				this.bulkBookingFilters.interval = 1;
			}
			if (!this.bulkBookingFilters.weekOrdinals) {
				this.bulkBookingFilters.weekOrdinals = [];
			}

			if (this.bulkBookingFilters.startDate && typeof this.bulkBookingFilters.startDate === 'string') {
				this.bulkBookingFilters.startDate = new Date(this.bulkBookingFilters.startDate);
			}
			if (this.bulkBookingFilters.endDate && typeof this.bulkBookingFilters.endDate === 'string') {
				this.bulkBookingFilters.endDate = new Date(this.bulkBookingFilters.endDate);
			}
			if (this.bulkBookingFilters.departureTime && typeof this.bulkBookingFilters.departureTime === 'string') {
				this.bulkBookingFilters.departureTime = new Date(this.bulkBookingFilters.departureTime);
			}
		}
	}

	get isBulkBookingFlow(): boolean {
		return !!this.bulkBookingFilters;
	}

	public compare(other: BookingWizardData<T>) {
		return JSON.stringify(this) === JSON.stringify(other);
	}
}

export function getDefaultInnerBookingWizardData(tabSelected: TabSelected): BookingWizardInnerDataType {
	switch (tabSelected) {
		case 'vehicle':
			return getDefaultVehicleWizardData();
		case 'freight':
			return getDefaultFreightWizardData();
		default:
			return getDefaultPassengerWizardData();
	}
}

/**
 * Compares two dynamic-field value maps. Missing maps/entries are treated the
 * same as empty strings so an untouched optional field (absent on one side,
 * '' on the other) doesn't register as a change.
 */
export function haveDynamicFieldValuesChanged(
	newValues?: Record<string, string>,
	oldValues?: Record<string, string>,
): boolean {
	const keys = new Set([...Object.keys(newValues ?? {}), ...Object.keys(oldValues ?? {})]);
	for (const key of keys) {
		if ((newValues?.[key] ?? '') !== (oldValues?.[key] ?? '')) {
			return true;
		}
	}
	return false;
}

// Used to check if any changes were made to a booking in the booking.
// This will be used to stop users from creating a new alteration on a booking
// if there have been no changes made.
export function ferryBookingWizardDataHasChanged(
	newWizardData: BookingWizardData,
	oldWizardData: BookingWizardData,
): boolean {
	// Shared checks (for vehicle, passenger and freight bookings)
	// Check if any of the journeys have changed
	if (newWizardData.departingJourney?.tripId !== oldWizardData.departingJourney?.tripId) {
		return true;
	}
	if (newWizardData.departingJourney?.startStopId !== oldWizardData.departingJourney?.startStopId) {
		return true;
	}
	if (newWizardData.departingJourney?.endStopId !== oldWizardData.departingJourney?.endStopId) {
		return true;
	}

	if (newWizardData.returningJourney?.tripId !== oldWizardData.returningJourney?.tripId) {
		return true;
	}
	if (newWizardData.returningJourney?.startStopId !== oldWizardData.returningJourney?.startStopId) {
		return true;
	}
	if (newWizardData.returningJourney?.endStopId !== oldWizardData.returningJourney?.endStopId) {
		return true;
	}

	// Check if ferry tickets have changed
	if (newWizardData.departureTicketId !== oldWizardData.departureTicketId) {
		return true;
	}
	if (newWizardData.returningTicketId !== oldWizardData.returningTicketId) {
		return true;
	}

	// Admin-configured dynamic field values live on every inner data type — a
	// dynamic-fields-only edit must register as a change or the alteration is
	// rejected as a duplicate ("No changes made to booking").
	if (
		haveDynamicFieldValuesChanged(
			newWizardData.innerWizardData?.dynamicFieldValues,
			oldWizardData.innerWizardData?.dynamicFieldValues,
		)
	) {
		return true;
	}

	// Check if passenger booking has changed
	if (
		newWizardData.innerWizardData instanceof PassengerBookingWizardData &&
		oldWizardData.innerWizardData instanceof PassengerBookingWizardData
	) {
		return passengerBookingWizardDataHasChanged(newWizardData.innerWizardData, oldWizardData.innerWizardData);
	}

	// Check if vehicle booking has changed
	if (
		newWizardData.innerWizardData instanceof VehicleBookingWizardData &&
		oldWizardData.innerWizardData instanceof VehicleBookingWizardData
	) {
		return vehicleBookingWizardDataHasChanged(newWizardData.innerWizardData, oldWizardData.innerWizardData);
	}

	// Check if freight booking has changed
	if (
		newWizardData.innerWizardData instanceof FreightBookingWizardData &&
		oldWizardData.innerWizardData instanceof FreightBookingWizardData
	) {
		return freightBookingWizardDataHasChanged(newWizardData.innerWizardData, oldWizardData.innerWizardData);
	}

	return false;
}
